import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";

const LeaveRequest = () => {
  // State for form data
  const [formData, setFormData] = useState({
    company: '',
    employee: '',
    reason: '',
    fromDate: '',
    toDate: '',
    startDate: '',
    endDate: '',
    contact: '',
    employeeName: '',
    leaveType: ''
  });

  // State for tracking if medical report should be shown
  const [showMedicalReport, setShowMedicalReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const employeeId = localStorage.getItem("employeeId");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // State for tracking which request is being edited
  const [editingId, setEditingId] = useState(null);

  // State for leave requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // State for leave balance
  const [leaveBalance, setLeaveBalance] = useState({
    casualLeave: { total: 12, used: 0, remaining: 12 },
    sickLeave: { total: 10, used: 0, remaining: 10 },
    annualLeave: { total: 20, used: 0, remaining: 20 }
  });

  // State for filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);

  // State for leave types
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
  const [leaveTypesError, setLeaveTypesError] = useState(null);

  // Fetch leave types from API
  const fetchLeaveTypes = async (companyIdParam = null) => {
    console.log('🚀 FETCHING LEAVE TYPES FOR COMPANY...');
    setLoadingLeaveTypes(true);
    setLeaveTypesError(null);
    
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      console.error('❌ No authentication token found');
      setLeaveTypesError('Authentication required. Please log in again.');
      setLoadingLeaveTypes(false);
      return;
    }

    let companyId = companyIdParam;
    
    // If no company ID passed as parameter, try to get from token
    if (!companyId) {
      try {
        const decoded = jwtDecode(token);
        console.log('🔍 LEAVE TYPES TOKEN DECODING:', {
          fullToken: decoded,
          companyId: decoded.companyId,
          companyObject: decoded.company,
          orgId: decoded.orgId
        });
        
        companyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId;
        console.log('🏢 EXTRACTED COMPANY ID:', companyId);
      } catch (err) {
        console.error("❌ Error decoding token for leave types:", err);
        setLeaveTypesError('Invalid authentication token. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        setLoadingLeaveTypes(false);
        return;
      }
    }

    // If still no company ID, try to get from employee details
    if (!companyId && employeeDetails?.company) {
      companyId = employeeDetails.company._id || employeeDetails.company;
      console.log('🏢 COMPANY ID FROM EMPLOYEE DETAILS:', companyId);
    }

    if (!companyId) {
      console.error('❌ No company ID available');
      setLeaveTypesError('Company information not found. Please contact support.');
      setLoadingLeaveTypes(false);
      return;
    }

    try {
      console.log('🚀 FETCHING LEAVE TYPES FROM API:', `https://customersupport-jhy9.onrender.com/api/leave-types/company/${companyId}`);
      
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/leave-types/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      console.log('📡 LEAVE TYPES API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });

      // Handle different response statuses
      if (response.status === 401) {
        console.error('🚫 Unauthorized - Token expired or invalid');
        setLeaveTypesError('Session expired. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        return;
      }

      if (response.status === 404) {
        console.warn('🔍 No leave types found for company');
        setLeaveTypesError('No leave types configured for your company. Please contact HR.');
        setLeaveTypes([]);
        return;
      }

      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}: ${response.statusText}`);
      }

      // Process successful response
      let fetchedLeaveTypes = [];
      
      if (response.data && Array.isArray(response.data)) {
        fetchedLeaveTypes = response.data;
      } else if (response.data.leaveTypes && Array.isArray(response.data.leaveTypes)) {
        fetchedLeaveTypes = response.data.leaveTypes;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        fetchedLeaveTypes = response.data.data;
      } else {
        console.warn("⚠️ Unexpected leave types response format:", response.data);
        setLeaveTypesError('Invalid response format from server.');
        setLeaveTypes([]);
        return;
      }

      console.log('🎯 PROCESSED LEAVE TYPES:', {
        count: fetchedLeaveTypes.length,
        leaveTypes: fetchedLeaveTypes.map(type => ({
          id: type._id || type.id,
          name: type.leaveType || type.name,
          description: type.description,
          daysAllowed: type.daysAllowed,
          carryForward: type.carryForward,
          active: type.active
        }))
      });

      setLeaveTypes(fetchedLeaveTypes);
      
      if (fetchedLeaveTypes.length === 0) {
        setLeaveTypesError('No leave types available. Please contact HR to configure leave types.');
      }

    } catch (err) {
      console.error("❌ Error fetching leave types:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // Set appropriate error message based on error type
      if (err.response?.status === 401) {
        setLeaveTypesError('Session expired. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
      } else if (err.response?.status === 404) {
        setLeaveTypesError('No leave types found for your company. Please contact HR.');
      } else if (err.response?.status >= 500) {
        setLeaveTypesError('Server error. Please try again later or contact support.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setLeaveTypesError('Network error. Please check your connection and try again.');
      } else {
        setLeaveTypesError('Failed to load leave types. Please try again.');
      }
      
      setLeaveTypes([]);
    } finally {
      setLoadingLeaveTypes(false);
    }
  };

  // Fetch employee details by ID
  const fetchEmployeeDetails = async (employeeId) => {
    if (!employeeId) return;

    setLoading(true);
    setError(null);
    const token = localStorage.getItem("authToken");

    if (!token) {
      setError("❌ Access token not found. Please log in.");
      setLoading(false);
      return;
    }

    // ✅ Decode companyId (if needed for validation/logging)
    let companyId;
    try {
      const decoded = jwtDecode(token);
      companyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId; // Extract company ID same as in console
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("❌ Invalid or expired token. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/employees/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const employeeData = response.data;
      setEmployeeDetails(employeeData);

      const fullName =
        employeeData.teamMemberName ||
        (employeeData.firstName && employeeData.lastName
          ? `${employeeData.firstName} ${employeeData.lastName}`
          : employeeData.name || "Employee");

      const employeeCompanyId = employeeData.company;
      
      setFormData((prev) => ({
        ...prev,
        employeeName: fullName,
        contact: employeeData.emergencyMobileNumber || "",
        company: employeeCompanyId || companyId,
      }));

      localStorage.setItem("employeeName", fullName);
      
      if (employeeCompanyId) {
        await fetchLeaveTypes(employeeCompanyId);
      }
    } catch (err) {
      console.error("Error fetching employee data:", err);
      setError("Failed to load employee details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch leave requests from API
  const fetchLeaveRequests = async () => {
    setLoadingRequests(true);
    setError(null);
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError("❌ Access token not found. Please log in again.");
      setLoadingRequests(false);
      return;
    }

    // ✅ Decode employeeId (and companyId if needed) from token
    let employeeId, companyId;
    try {
      const decoded = jwtDecode(token);
      employeeId = decoded.employeeId || decoded.id || decoded._id; // depends on backend payload
      companyId = decoded.companyId || decoded.orgId; // optional, if API needs company filter
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("❌ Invalid or expired token. Please log in again.");
      setLoadingRequests(false);
      return;
    }

    if (!employeeId) {
      setError("❌ No employee ID found in token.");
      setLoadingRequests(false);
      return;
    }

    try {
      // Fetch all leave requests for this employee
      const response = await axios.get(
        "https://customersupport-jhy9.onrender.com/api/leaves",
        {
          params: { employee: employeeId, company: companyId }, // include company if API supports it
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          validateStatus: (status) => status < 500,
        }
      );

      if (response.status === 200) {
        let leaveRequests = [];

        if (response.data.leaves && Array.isArray(response.data.leaves)) {
          leaveRequests = response.data.leaves;
        } else if (Array.isArray(response.data)) {
          leaveRequests = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          leaveRequests = response.data.data;
        }

        setLeaveRequests(leaveRequests);
        setFilteredLeaveRequests(leaveRequests);
        calculateLeaveBalance(leaveRequests);
      } else {
        throw new Error(
          response.data?.message ||
            `Failed to fetch leave requests (${response.status})`
        );
      }
    } catch (err) {
      console.error("Error in fetchLeaveRequests:", err);
      setError(err.message || "Failed to load leave requests. Please try again.");
    } finally {
      setLoadingRequests(false);
    }
  };

  // Calculate leave balance from leave requests
  const calculateLeaveBalance = (requests) => {
    if (!Array.isArray(requests)) {
      console.warn('Invalid leave requests data:', requests);
      return;
    }

    const today = new Date();
    const currentYear = today.getFullYear();

    const balance = {
      casualLeave: { used: 0 },
      sickLeave: { used: 0 },
      annualLeave: { used: 0 }
    };

    requests.forEach(request => {
      if (!request) return;
      
      // Get the leave type from the request
      const leaveType = (request.leaveType || request.type || '').toString().toLowerCase();
      const status = (request.status || '').toString().toLowerCase();
      const startDate = request.startDate || request.fromDate;
      const endDate = request.endDate || request.toDate;

      // Only process approved leave requests with valid dates
      if (status === 'approved' && startDate && endDate) {
        try {
          const start = new Date(startDate);
          const end = new Date(endDate);

          // Only count leaves from current year
          if (start.getFullYear() === currentYear) {
            const days = calculateDays(start, end);

            // Categorize the leave type
            if (leaveType.includes('casual')) {
              balance.casualLeave.used += days;
            } else if (leaveType.includes('sick')) {
              balance.sickLeave.used += days;
            } else if (leaveType.includes('annual') || leaveType.includes('paid') || leaveType === 'leave') {
              // Default to annual leave if type is just 'leave' or not specified
              balance.annualLeave.used += days;
            } else {
              console.warn('Unknown leave type:', leaveType);
            }
          }
        } catch (error) {
          console.error('Error processing leave request:', { 
            request: { 
              id: request._id || request.id,
              leaveType: request.leaveType || request.type,
              status: request.status,
              startDate: request.startDate || request.fromDate,
              endDate: request.endDate || request.toDate
            }, 
            error 
          });
        }
      }
    });

    // Define leave limits (you can make these configurable)
    const leaveLimits = {
      casualLeave: 12,
      sickLeave: 10,
      annualLeave: 20
    };

    // Calculate remaining days for each leave type
    const newLeaveBalance = {
      casualLeave: {
        total: leaveLimits.casualLeave,
        used: Math.min(balance.casualLeave.used, leaveLimits.casualLeave),
        remaining: Math.max(0, leaveLimits.casualLeave - balance.casualLeave.used)
      },
      sickLeave: {
        total: leaveLimits.sickLeave,
        used: Math.min(balance.sickLeave.used, leaveLimits.sickLeave),
        remaining: Math.max(0, leaveLimits.sickLeave - balance.sickLeave.used)
      },
      annualLeave: {
        total: leaveLimits.annualLeave,
        used: Math.min(balance.annualLeave.used, leaveLimits.annualLeave),
        remaining: Math.max(0, leaveLimits.annualLeave - balance.annualLeave.used)
      }
    };

    setLeaveBalance(newLeaveBalance);
  };

  // Load employee data
  const loadEmployeeData = async () => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError("No access token found. Please log in again.");
      return;
    }

    let employeeId;
    try {
      const decoded = jwtDecode(token);
      const extractedCompanyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId;
      
      employeeId = decoded.employeeId || decoded.id || decoded._id;
      
      if (extractedCompanyId) {
        setFormData(prev => ({
          ...prev,
          company: extractedCompanyId
        }));
      }
      
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Invalid or expired token. Please log in again.");
      return;
    }

    if (employeeId) {
      await fetchEmployeeDetails(employeeId);
    } else {
      setError("No employee ID found. Please log in again.");
    }
  };

  // Load employee details and leave requests on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load employee data (this will also fetch leave types with company ID)
        await loadEmployeeData();
        // Load leave requests
        await fetchLeaveRequests();
        // Note: fetchLeaveTypes() is now called from fetchEmployeeDetails() with proper company ID
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const employeeId = localStorage.getItem("selectedEmployeeId");
      const companyId = localStorage.getItem("selectedCompanyId");

      if (!token || !employeeId) {
        // Optionally redirect to login
        // navigate('/login');
      } else {
        // Update the state with the correct IDs
        setSelectedEmployeeId(employeeId);
        setFormData(prev => ({
          ...prev,
          company: companyId,
          employee: employeeId
        }));
      }
    };

    checkAuth();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle edit action
  const handleEdit = (id) => {
    setEditingId(id);
    const requestToEdit = leaveRequests.find(request => {
      const requestId = request._id || request.id;
      return requestId === id;
    });
    
    if (requestToEdit) {
      setFormData({
        ...formData,
        startDate: requestToEdit.startDate || requestToEdit.fromDate || '',
        endDate: requestToEdit.endDate || requestToEdit.toDate || '',
        reason: requestToEdit.reason || '',
        contact: requestToEdit.contact || employeeDetails?.emergencyMobileNumber || '',
        leaveType: requestToEdit.leaveType || requestToEdit.type || ''
      });
    }
  };

  // Cancel edit mode
  const cancelEdit = () => {
    setEditingId(null);
    setFormData(prev => ({
      ...prev,
      startDate: '',
      endDate: '',
      reason: '',
      fromDate: '',
      toDate: '',
      leaveType: ''
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check for authentication token
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");

      // Validation
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Decode JWT token to extract employeeId and companyId
      let employeeId, companyId;
      try {
        const decoded = jwtDecode(token);
        
        // Extract employeeId - this should be the logged-in user
        employeeId = decoded.employeeId || decoded._id || decoded.id;
        
        // Extract companyId - look for company information in different possible fields
        companyId = decoded.companyId || 
                   decoded.company?._id || 
                   decoded.company?.id || 
                   decoded.company ||
                   decoded.orgId ||
                   decoded.organizationId;
        
        // If no separate company ID found, we need to get it from employee data
        if (!companyId && employeeDetails?.company) {
          companyId = employeeDetails.company._id || employeeDetails.company;
        }
        
      } catch (err) {
        console.error("Error decoding token:", err);
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error('Invalid or expired token. Please log in again.');
      }

      if (!employeeId) {
        throw new Error('Employee ID is missing from token. Please log in again.');
      }
      
      // If companyId is still not found, try to get it from localStorage or fetch employee details
      if (!companyId) {
        
        // Try localStorage first
        const storedCompanyId = localStorage.getItem('selectedCompanyId');
        if (storedCompanyId) {
          companyId = storedCompanyId;
        } else {
          // If still no companyId, we need to fetch employee details to get company info
          try {
            const empResponse = await axios.get(
              `https://customersupport-jhy9.onrender.com/api/employees/${employeeId}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            
            if (empResponse.data?.company) {
              companyId = empResponse.data.company._id || empResponse.data.company;
            }
          } catch (empErr) {
            console.error('Failed to fetch employee details for company ID:', empErr);
          }
        }
      }
      
      if (!companyId) {
        throw new Error('Company information is missing. Please contact support.');
      }

      // Validate form fields
      if (!formData.reason || !formData.reason.trim()) {
        throw new Error('Please provide a reason for your leave request.');
      }

      const startDate = formData.fromDate || formData.startDate;
      const endDate = formData.toDate || formData.endDate;

      if (!startDate || !endDate) {
        throw new Error('Please select both start and end dates for your leave.');
      }

      if (new Date(startDate) > new Date(endDate)) {
        throw new Error('End date must be after or equal to start date.');
      }

      setSubmitting(true);
      setError(null);

      // Prepare request data as JSON object with proper field names
      const requestData = {
        company: companyId,
        employee: employeeId,
        reason: formData.reason.trim(),
        fromDate: startDate,
        toDate: endDate,
        contact: formData.contact || '',
        leaveType: formData.leaveType || 'Leave', // Selected leave type or default
        status: 'Pending', // Default status
        appliedDate: new Date().toISOString()
      };

      // Send the request
      const response = await axios.post(
        'https://customersupport-jhy9.onrender.com/api/leaves',
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Handle successful submission

      // Reset the form first
      setFormData({
        company: companyId,
        employee: employeeId,
        reason: '',
        fromDate: '',
        toDate: '',
        startDate: '',
        endDate: '',
        contact: formData.contact, // Keep the contact info
        employeeName: formData.employeeName, // Keep the employee name
        leaveType: '' // Reset leave type
      });

      // Show success message
      alert('Leave request submitted successfully!');
      
      // Refresh the leave requests after a short delay to ensure the backend has processed the new request
      setTimeout(() => {
        fetchLeaveRequests().catch(err => {
          console.error('Error refreshing leave requests:', err);
          setError('Leave submitted but failed to refresh the list. Please refresh the page.');
        });
      }, 500);

    } catch (err) {
      console.error('Error submitting leave request:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        fullError: err
      });

      // Log the full response for debugging
      if (err.response) {
        console.error('Full API response:', err.response);
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        console.error('Response headers:', err.response.headers);
      }

      let errorMessage = 'Failed to submit leave request';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data) {
        errorMessage = JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete action
  const handleDelete = async (id) => {
    if (!id) {
      console.error('Invalid leave request ID');
      setError('Invalid leave request ID');
      return;
    }

    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        const token = localStorage.getItem("authToken") || localStorage.getItem("token");
        if (!token) {
          throw new Error('No authentication token found');
        }

        // First find the request to ensure it exists
        const requestToDelete = leaveRequests.find(request => {
          const requestId = request._id || request.id;
          return requestId === id;
        });

        if (!requestToDelete) {
          throw new Error('Leave request not found');
        }

        // Use the correct endpoint and method
        await axios.delete(`https://customersupport-jhy9.onrender.com/api/leaves/${id}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Refresh the leave requests
        await fetchLeaveRequests();
        alert('Leave request deleted successfully!');
      } catch (err) {
        console.error("Error deleting leave request:", {
          error: err.message,
          response: err.response?.data
        });
        setError(err.response?.data?.message || 'Failed to delete leave request. Please try again.');
      }
    }
  };

  // Calculate number of days between dates
  const calculateDays = (start, end) => {
    try {
      // Handle both string and Date objects
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      
      // Check if dates are valid
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid date range:', { start, end });
        return 0;
      }
      
      // Calculate difference in days (inclusive of both start and end dates)
      const diffTime = Math.abs(endDate - startDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      // Ensure we don't return negative days
      return Math.max(0, days);
    } catch (error) {
      console.error('Error calculating days between dates:', { start, end, error });
      return 0;
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#28C76F';
      case 'Pending': return '#FF9F43';
      case 'Rejected': return '#EA5455';
      default: return '#6E6E6E';
    }
  };

  // Filter leave requests by month and year
  const filterLeaveRequests = () => {
    let filtered = [...leaveRequests];
    
    if (filterMonth || filterYear) {
      filtered = leaveRequests.filter(request => {
        if (!request) return false;
        
        const startDate = request.fromDate || request.startDate;
        if (!startDate) return false;
        
        try {
          const requestDate = new Date(startDate);
          const requestMonth = requestDate.getMonth() + 1; // getMonth() returns 0-11
          const requestYear = requestDate.getFullYear();
          
          let matchesMonth = true;
          let matchesYear = true;
          
          if (filterMonth) {
            matchesMonth = requestMonth === parseInt(filterMonth);
          }
          
          if (filterYear) {
            matchesYear = requestYear === parseInt(filterYear);
          }
          
          return matchesMonth && matchesYear;
        } catch (error) {
          console.error('Error filtering request:', error);
          return false;
        }
      });
    }
    
    setFilteredLeaveRequests(filtered);
  };

  // Handle filter changes
  const handleMonthChange = (e) => {
    setFilterMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setFilterYear(e.target.value);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterMonth('');
    setFilterYear('');
    setFilteredLeaveRequests(leaveRequests);
  };

  // Generate year options (current year and past 5 years)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // Apply filters when filter values or leave requests change
  React.useEffect(() => {
    filterLeaveRequests();
  }, [filterMonth, filterYear, leaveRequests]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>Leave Management</h2>
        <p style={styles.subtitle}>Manage your leave requests and view leave balance</p>
      </div>

      <div style={styles.content}>
        {/* Leave Request Form */}
        <div style={styles.formSection}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitle}>
              <div style={styles.cardIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7367F0" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              {editingId ? 'Edit Leave Request' : 'Apply for Leave'}
            </div>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Employee Name</label>
                <div style={styles.readOnlyField}>
                  <span style={styles.employeeName}>
                    {formData.employeeName || 'Loading...'}
                  </span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Contact During Leave</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  style={styles.input}
                  placeholder="Enter phone number"
                  required
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Leave Type
                  {loadingLeaveTypes && (
                    <span style={styles.loadingIndicator}>
                      <div style={styles.spinner}></div>
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleInputChange}
                  style={{
                    ...styles.select,
                    opacity: loadingLeaveTypes ? 0.7 : 1,
                    cursor: loadingLeaveTypes ? 'not-allowed' : 'pointer'
                  }}
                  disabled={loadingLeaveTypes}
                  required
                >
                  <option value="">
                    {loadingLeaveTypes 
                      ? 'Loading leave types...' 
                      : leaveTypesError 
                        ? 'Error loading leave types'
                        : leaveTypes.length === 0
                          ? 'No leave types available'
                          : 'Select Leave Type'
                    }
                  </option>
                  {!loadingLeaveTypes && !leaveTypesError && leaveTypes.map(type => (
                    <option key={type._id || type.id} value={type.leaveType || type.name}>
                      {type.leaveType || type.name}
                      {type.daysAllowed && ` (${type.daysAllowed} days)`}
                    </option>
                  ))}
                </select>
                {leaveTypesError && (
                  <div style={styles.errorAlert}>
                    <strong>⚠️ Error:</strong> {leaveTypesError}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Reason for Leave</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Please provide a detailed reason for your leave request"
                rows="4"
                required
              />
            </div>

            {error && (
              <div style={styles.errorMessage}>
                <div style={styles.errorIcon}>⚠️</div>
                <div>{error}</div>
              </div>
            )}

            <div style={styles.buttonGroup}>
              <button 
                type="submit" 
                style={{
                  ...styles.submitButton,
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div style={styles.buttonSpinner}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ marginRight: '8px' }}>
                      <path d="m3 16 4 4 4-4"></path>
                      <path d="m7 20 0-16"></path>
                      <path d="m11 4 4 4 4-4"></path>
                      <path d="m15 8 0 8"></path>
                    </svg>
                    {editingId ? 'Update Request' : 'Submit Request'}
                  </>
                )}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  style={styles.cancelButton}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Leave Balance Cards */}
        <div style={styles.balanceSection}>
         {/* Leave History */}
        <div style={styles.historySection}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitle}>
              <div style={styles.cardIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#775DD0" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
              Leave History
            </div>
            <div style={styles.requestsCount}>
              {filteredLeaveRequests.length} of {leaveRequests.length} requests
            </div>
          </div>
          
          {/* Filter Controls */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Month</label>
              <select 
                value={filterMonth} 
                onChange={handleMonthChange}
                style={styles.filterSelect}
              >
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Year</label>
              <select 
                value={filterYear} 
                onChange={handleYearChange}
                style={styles.filterSelect}
              >
                <option value="">All Years</option>
                {getYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            {(filterMonth || filterYear) && (
              <button 
                onClick={clearFilters}
                style={styles.clearFiltersButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Clear
              </button>
            )}
          </div>

          {loadingRequests ? (
            <div style={styles.loadingState}>
              <div style={styles.loadingSpinner}></div>
              <span>Loading leave requests...</span>
            </div>
          ) : error ? (
            <div style={styles.errorMessage}>
              <div style={styles.errorIcon}>⚠️</div>
              <div>{error}</div>
            </div>
          ) : filteredLeaveRequests.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📝</div>
              <div style={styles.emptyTitle}>
                {leaveRequests.length === 0 ? 'No Leave Requests' : 'No Matching Requests'}
              </div>
              <div style={styles.emptySubtitle}>
                {leaveRequests.length === 0 
                  ? 'Start by submitting your first leave request above' 
                  : 'Try adjusting your filters to see more results'
                }
              </div>
            </div>
          ) : (
            <div style={styles.requestsList}>
              {filteredLeaveRequests.map(request => {
                if (!request) return null;
                
                try {
                  const requestId = request._id || request.id || Math.random().toString(36).substr(2, 9);
                  const leaveType = request.leaveType || request.type || 'Leave';
                  const status = request.status || 'Pending';
                  const startDate = request.fromDate || request.startDate || new Date().toISOString();
                  const endDate = request.toDate || request.endDate || new Date().toISOString();
                  const reason = request.reason || 'No reason provided';
                  
                  const formatDate = (dateString) => {
                    try {
                      return new Date(dateString).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } catch (e) {
                      return 'Invalid date';
                    }
                  };
                  
                  const appliedDate = request.appliedDate || request.createdAt || new Date().toISOString();

                  return (
                    <div key={requestId} style={styles.requestCard}>
                      <div style={styles.requestHeader}>
                        <div style={styles.requestInfo}>
                          <div style={styles.requestTypeContainer}>
                            <span style={styles.requestType}>{leaveType}</span>
                            <span 
                              style={{
                                ...styles.requestStatus,
                                backgroundColor: getStatusColor(status)
                              }}
                            >
                              {status}
                            </span>
                          </div>
                          <div style={styles.appliedDate}>
                            Applied on {formatDate(appliedDate)}
                          </div>
                        </div>
                        <div style={styles.durationBadge}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#008FFB" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12,6 12,12 16,14"></polyline>
                          </svg>
                          {calculateDays(startDate, endDate)} {calculateDays(startDate, endDate) === 1 ? 'Day' : 'Days'}
                        </div>
                      </div>

                      <div style={styles.dateRange}>
                        <div style={styles.dateItem}>
                          <div style={styles.dateLabel}>From</div>
                          <div style={styles.dateValue}>{formatDate(startDate)}</div>
                        </div>
                        <div style={styles.dateArrow}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9E9E9E" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12,5 19,12 12,19"></polyline>
                          </svg>
                        </div>
                        <div style={styles.dateItem}>
                          <div style={styles.dateLabel}>To</div>
                          <div style={styles.dateValue}>{formatDate(endDate)}</div>
                        </div>
                      </div>

                      <div style={styles.reasonSection}>
                        <div style={styles.reasonLabel}>Reason</div>
                        <div style={styles.reasonText}>{reason}</div>
                      </div>

                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => handleEdit(requestId)}
                          style={{
                            ...styles.actionButton,
                            ...styles.editButton,
                            opacity: status === 'Approved' ? 0.5 : 1,
                            cursor: status === 'Approved' ? 'not-allowed' : 'pointer'
                          }}
                          disabled={status === 'Approved'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(requestId)}
                          style={{
                            ...styles.actionButton,
                            ...styles.deleteButton,
                            opacity: status === 'Approved' ? 0.5 : 1,
                            cursor: status === 'Approved' ? 'not-allowed' : 'pointer'
                          }}
                          disabled={status === 'Approved'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6 v14 a2,2 0 0,1 -2,2 H7 a2,2 0 0,1 -2,-2 V6 m3,0 V4 a2,2 0 0,1 2,-2 h4 a2,2 0 0,1 2,2 v2"></path>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                } catch (error) {
                  console.error('Error rendering leave request:', { request, error });
                  return null;
                }
              })}
            </div>
          )}
        </div>
        </div>

        
      </div>
    </div>
  );
};

// CSS-in-JS styles with the modern color scheme
const styles = {
  container: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    backgroundColor: '#F8F9FA',
    minHeight: '100vh',
    padding: '24px'
  },
  
  header: {
    marginBottom: '32px',
    textAlign: 'center'
  },
  
  heading: {
    color: '#2C2C2C',
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em'
  },
  
  subtitle: {
    color: '#6E6E6E',
    fontSize: '16px',
    margin: '0',
    fontWeight: '400'
  },
  
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
    alignItems: 'start',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '1fr',
      gap: '20px'
    }
  },
  
  formSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '0',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    border: '1px solid #F5F6FA',
    overflow: 'hidden'
  },
  
  balanceSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '0',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    border: '1px solid #F5F6FA',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  
  historySection: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '0',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    border: '1px solid #F5F6FA',
    overflow: 'hidden',
    gridColumn: '1 / -1'
  },
  
  cardHeader: {
    background: 'linear-gradient(135deg, #7367F0 0%, #5A67D8 100%)',
    padding: '24px',
    borderBottom: '1px solid #F5F6FA'
  },
  
  cardTitle: {
    color: '#FFFFFF',
    fontSize: '20px',
    fontWeight: '600',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  
  cardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '10px'
  },
  
  form: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr'
    }
  },
  
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  
  label: {
    color: '#333333',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  
  input: {
    padding: '14px 16px',
    border: '2px solid #F5F6FA',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '400',
    color: '#333333',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit',
    ':focus': {
      borderColor: '#7367F0',
      boxShadow: '0 0 0 4px rgba(115, 103, 240, 0.1)'
    }
  },
  
  select: {
    padding: '14px 16px',
    border: '2px solid #F5F6FA',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '400',
    color: '#333333',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit',
    ':focus': {
      borderColor: '#7367F0',
      boxShadow: '0 0 0 4px rgba(115, 103, 240, 0.1)'
    }
  },
  
  textarea: {
    padding: '14px 16px',
    border: '2px solid #F5F6FA',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '400',
    color: '#333333',
    backgroundColor: '#FFFFFF',
    resize: 'vertical',
    minHeight: '100px',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit',
    ':focus': {
      borderColor: '#7367F0',
      boxShadow: '0 0 0 4px rgba(115, 103, 240, 0.1)'
    }
  },
  
  readOnlyField: {
    padding: '14px 16px',
    backgroundColor: '#F8F9FA',
    border: '2px solid #F5F6FA',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center'
  },
  
  employeeName: {
    color: '#333333',
    fontSize: '16px',
    fontWeight: '500'
  },
  
  loadingIndicator: {
    color: '#7367F0',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: '8px'
  },
  
  spinner: {
    width: '12px',
    height: '12px',
    border: '2px solid #E5E7EB',
    borderTop: '2px solid #7367F0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  
  errorAlert: {
    marginTop: '8px',
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    color: '#DC2626',
    fontSize: '14px'
  },
  
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '12px',
    color: '#DC2626',
    fontSize: '14px',
    fontWeight: '500'
  },
  
  errorIcon: {
    fontSize: '18px'
  },
  
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 24px',
    backgroundColor: '#7367F0',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    gap: '8px',
    ':hover': {
      backgroundColor: '#5A67D8',
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 24px rgba(115, 103, 240, 0.3)'
    }
  },
  
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 24px',
    backgroundColor: '#F8F9FA',
    color: '#6E6E6E',
    border: '2px solid #F5F6FA',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    gap: '8px',
    ':hover': {
      backgroundColor: '#E5E7EB',
      borderColor: '#E5E7EB'
    }
  },
  
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid #FFFFFF',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px'
  },
  
  balanceGrid: {
    padding: '24px',
    display: 'grid',
    gap: '16px'
  },
  
  balanceCard: {
    padding: '20px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
    border: '1px solid #F5F6FA',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F1F3F4',
      transform: 'translateY(-2px)'
    }
  },
  
  balanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  
  balanceType: {
    color: '#333333',
    fontSize: '14px',
    fontWeight: '600'
  },
  
  balanceIcon: {
    fontSize: '20px'
  },
  
  balanceNumbers: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    marginBottom: '8px'
  },
  
  balanceUsed: {
    color: '#7367F0',
    fontSize: '28px',
    fontWeight: '700'
  },
  
  balanceTotal: {
    color: '#6E6E6E',
    fontSize: '18px',
    fontWeight: '500'
  },
  
  balanceRemaining: {
    color: '#6E6E6E',
    fontSize: '12px',
    fontWeight: '500'
  },
  
  requestsCount: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: '#FFFFFF',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  
  filterContainer: {
    padding: '24px 24px 0 24px',
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  },
  
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '140px'
  },
  
  filterLabel: {
    color: '#333333',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  
  filterSelect: {
    padding: '10px 12px',
    border: '2px solid #F5F6FA',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333333',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    ':focus': {
      borderColor: '#7367F0'
    }
  },
  
  clearFiltersButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    backgroundColor: '#6E6E6E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#333333'
    }
  },
  
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '48px',
    color: '#6E6E6E',
    fontSize: '16px'
  },
  
  loadingSpinner: {
    width: '24px',
    height: '24px',
    border: '3px solid #F5F6FA',
    borderTop: '3px solid #7367F0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#6E6E6E'
  },
  
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333333',
    marginBottom: '8px'
  },
  
  emptySubtitle: {
    fontSize: '14px',
    color: '#6E6E6E'
  },
  
  requestsList: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  
  requestCard: {
    padding: '24px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #F5F6FA',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)'
    }
  },
  
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  
  requestInfo: {
    flex: 1
  },
  
  requestTypeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px'
  },
  
  requestType: {
    color: '#333333',
    fontSize: '18px',
    fontWeight: '600'
  },
  
  requestStatus: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#FFFFFF',
    textTransform: 'capitalize'
  },
  
  appliedDate: {
    color: '#6E6E6E',
    fontSize: '14px',
    fontWeight: '500'
  },
  
  durationBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#F0F8FF',
    border: '1px solid #E1F5FE',
    borderRadius: '20px',
    color: '#008FFB',
    fontSize: '14px',
    fontWeight: '600'
  },
  
  dateRange: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
    marginBottom: '16px'
  },
  
  dateItem: {
    textAlign: 'center'
  },
  
  dateLabel: {
    color: '#6E6E6E',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px'
  },
  
  dateValue: {
    color: '#333333',
    fontSize: '16px',
    fontWeight: '600'
  },
  
  dateArrow: {
    display: 'flex',
    alignItems: 'center'
  },
  
  reasonSection: {
    marginBottom: '20px'
  },
  
  reasonLabel: {
    color: '#333333',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px'
  },
  
  reasonText: {
    color: '#6E6E6E',
    fontSize: '14px',
    lineHeight: '1.6',
    padding: '12px 16px',
    backgroundColor: '#F8F9FA',
    borderRadius: '8px',
    border: '1px solid #F5F6FA'
  },
  
  actionButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #F5F6FA'
  },
  
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none'
  },
  
  editButton: {
    backgroundColor: '#FFF4E6',
    color: '#FF9F43',
    border: '1px solid #FFE0B2',
    ':hover': {
      backgroundColor: '#FFE0B2',
      transform: 'translateY(-1px)'
    }
  },
  
  deleteButton: {
    backgroundColor: '#FFEBEE',
    color: '#EA5455',
    border: '1px solid #FFCDD2',
 ':hover': {
      backgroundColor: '#FFCDD2',
      transform: 'translateY(-1px)'
    }
  }
};

// Add keyframes animation for spinners
const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject keyframes
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = keyframes;
  document.head.appendChild(styleElement);
}

export default LeaveRequest;
